/**
 * Matrix Reducer - Consolidated state management for DisciplineMatrix
 * 
 * Replaces 80+ individual useState hooks with a single useReducer.
 * This dramatically reduces React reconciliation overhead.
 */

/**
 * Initial state structure for the matrix
 */
export const initialState = {
  // Data
  data: {
    rows: [],           // Transformed matrix data for display
    original: [],       // Original data for change detection
    priorities: [],     // Priority options for dropdown
    fabricationLocations: [], // Fabrication shop options for dropdown
    columnMappings: [], // Custom column mappings from project config
  },
  
  // Loading states
  loading: {
    data: true,
    priorities: false,
    saving: false,
    exporting: false,
    projectDetails: true,
  },
  
  // Modal visibility
  modals: {
    upload: false,
    actualSpentUpload: false,
    export: false,
    columnConfig: false,
    matBore: false,
    progressTrends: false,
    manageTeam: false,
    manageUsers: false,
  },
  
  // UI state
  ui: {
    columnFilters: {},      // { columnId: [selectedValues] }
    textFilters: {},        // { columnId: { mode: string, text: string } }
    openFilterColumn: null, // Currently open filter dropdown
    sortConfig: { key: null, direction: 'asc' }, // Sorting configuration
    searchQuery: '',        // Global search query
    selectedMatrixId: null, // Selected row for progress trends
    selectedLineTag: null,  // Selected line tag for progress trends
    hasLoadedOnce: false,   // Whether initial load has completed
    isRefreshingAfterUpload: false,
    collapsedGroups: {},    // { groupName: true } for collapsed column groups
    selectedRows: new Set(), // Set of row IDs selected for batch operations (delete, etc.)
  },
  
  // Background job tracking
  jobs: {
    active: null,     // Active upload job ID
    save: null,       // Active save job ID
    export: null,     // Active export job ID
  },
  
  // Toast notifications
  toast: {
    show: false,
    message: '',
    type: 'success', // 'success' | 'error' | 'warning' | 'info'
  },
  
  // Change tracking
  changedRows: new Set(), // Set of row IDs with unsaved changes
  hasUnsavedChanges: false,
  
  // Project context
  project: {
    details: null,
    id: null,
  },
  
  // Permissions (pt_* = Progress Tracking module)
  permissions: {
    edit: false,        // pt_edit
    delete: false,      // pt_delete
    analytics: false,   // pt_analytics
    import: false,      // pt_import
    export: false,      // pt_export
    template: false,    // pt_template
    columnConfig: false, // pt_columnconfig
    roc: false,         // pt_roc
    sfRatio: false,     // pt_sfratio
    matBore: false,     // pt_matbore
    isAdmin: false,     // True for superadmin/admin - bypasses all checks
    isSuperadmin: false, // True only for superadmin - gates Show-deleted / Restore / Purge
    loading: true,
  },
};

/**
 * Action types for the matrix reducer
 */
export const ActionTypes = {
  // Data actions
  SET_DATA: 'SET_DATA',
  SET_ROWS: 'SET_ROWS',
  SET_ORIGINAL_DATA: 'SET_ORIGINAL_DATA',
  SET_PRIORITIES: 'SET_PRIORITIES',
  SET_FABRICATION_LOCATIONS: 'SET_FABRICATION_LOCATIONS',
  SET_COLUMN_MAPPINGS: 'SET_COLUMN_MAPPINGS',
  UPDATE_ROW: 'UPDATE_ROW',
  UPDATE_ROW_BY_ID: 'UPDATE_ROW_BY_ID',
  
  // Loading actions
  SET_LOADING: 'SET_LOADING',
  
  // Modal actions
  OPEN_MODAL: 'OPEN_MODAL',
  CLOSE_MODAL: 'CLOSE_MODAL',
  TOGGLE_MODAL: 'TOGGLE_MODAL',
  
  // UI actions
  SET_COLUMN_FILTER: 'SET_COLUMN_FILTER',
  CLEAR_COLUMN_FILTER: 'CLEAR_COLUMN_FILTER',
  SET_ALL_COLUMN_FILTERS: 'SET_ALL_COLUMN_FILTERS',
  SET_TEXT_FILTER: 'SET_TEXT_FILTER',
  CLEAR_TEXT_FILTER: 'CLEAR_TEXT_FILTER',
  CLEAR_ALL_FILTERS: 'CLEAR_ALL_FILTERS',
  TOGGLE_COLUMN_GROUP: 'TOGGLE_COLUMN_GROUP',
  SET_COLLAPSED_GROUPS: 'SET_COLLAPSED_GROUPS',
  SET_OPEN_FILTER_COLUMN: 'SET_OPEN_FILTER_COLUMN',
  SET_SORT_CONFIG: 'SET_SORT_CONFIG',
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
  SET_SELECTED_MATRIX: 'SET_SELECTED_MATRIX',
  SET_UI: 'SET_UI',
  
  // Job actions
  SET_JOB: 'SET_JOB',
  CLEAR_JOB: 'CLEAR_JOB',
  
  // Toast actions
  SHOW_TOAST: 'SHOW_TOAST',
  HIDE_TOAST: 'HIDE_TOAST',
  
  // Change tracking
  ADD_CHANGED_ROW: 'ADD_CHANGED_ROW',
  REMOVE_CHANGED_ROW: 'REMOVE_CHANGED_ROW',
  REMOVE_CHANGED_ROWS: 'REMOVE_CHANGED_ROWS',
  CLEAR_CHANGED_ROWS: 'CLEAR_CHANGED_ROWS',
  SET_CHANGED_ROWS: 'SET_CHANGED_ROWS',
  
  // Project actions
  SET_PROJECT_DETAILS: 'SET_PROJECT_DETAILS',
  SET_PROJECT_ID: 'SET_PROJECT_ID',
  
  // Permission actions
  SET_PERMISSIONS: 'SET_PERMISSIONS',
  SET_PERMISSION: 'SET_PERMISSION',
  
  // Batch update for multiple state changes at once
  BATCH_UPDATE: 'BATCH_UPDATE',
  
  // Reset state
  RESET: 'RESET',
  
  // Row selection actions (for batch delete, etc.)
  TOGGLE_ROW_SELECTION: 'TOGGLE_ROW_SELECTION',
  SELECT_ALL_ROWS: 'SELECT_ALL_ROWS',
  CLEAR_SELECTION: 'CLEAR_SELECTION',
  SET_SELECTED_ROWS: 'SET_SELECTED_ROWS',
  
  // Row removal (after delete)
  REMOVE_ROWS: 'REMOVE_ROWS',
};

/**
 * Matrix reducer function
 */
export function matrixReducer(state, action) {
  switch (action.type) {
    // Data actions
    case ActionTypes.SET_DATA:
      return {
        ...state,
        data: { ...state.data, ...action.payload },
      };
    
    case ActionTypes.SET_ROWS:
      return {
        ...state,
        data: { ...state.data, rows: action.payload },
      };
    
    case ActionTypes.SET_ORIGINAL_DATA:
      return {
        ...state,
        data: { ...state.data, original: action.payload },
      };
    
    case ActionTypes.SET_PRIORITIES:
      return {
        ...state,
        data: { ...state.data, priorities: action.payload },
      };
    
    case ActionTypes.SET_FABRICATION_LOCATIONS:
      return {
        ...state,
        data: { ...state.data, fabricationLocations: action.payload },
      };
    
    case ActionTypes.SET_COLUMN_MAPPINGS:
      return {
        ...state,
        data: { ...state.data, columnMappings: action.payload },
      };
    
    case ActionTypes.UPDATE_ROW: {
      const { rowIndex, columnId, value } = action.payload;
      const newRows = [...state.data.rows];
      if (newRows[rowIndex]) {
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          [columnId]: value,
        };
      }
      return {
        ...state,
        data: { ...state.data, rows: newRows },
      };
    }
    
    case ActionTypes.UPDATE_ROW_BY_ID: {
      const { rowId, columnId, value } = action.payload;
      const newRows = state.data.rows.map(row => {
        if (row.id === rowId) {
          return { ...row, [columnId]: value };
        }
        return row;
      });
      return {
        ...state,
        data: { ...state.data, rows: newRows },
      };
    }
    
    // Loading actions
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: { ...state.loading, [action.key]: action.value },
      };
    
    // Modal actions
    case ActionTypes.OPEN_MODAL:
      return {
        ...state,
        modals: { ...state.modals, [action.modal]: true },
      };
    
    case ActionTypes.CLOSE_MODAL:
      return {
        ...state,
        modals: { ...state.modals, [action.modal]: false },
      };
    
    case ActionTypes.TOGGLE_MODAL:
      return {
        ...state,
        modals: { ...state.modals, [action.modal]: !state.modals[action.modal] },
      };
    
    // UI actions
    case ActionTypes.SET_COLUMN_FILTER: {
      const { columnId, values } = action.payload;
      return {
        ...state,
        ui: {
          ...state.ui,
          columnFilters: {
            ...state.ui.columnFilters,
            [columnId]: values,
          },
        },
      };
    }
    
    case ActionTypes.CLEAR_COLUMN_FILTER: {
      const newFilters = { ...state.ui.columnFilters };
      delete newFilters[action.columnId];
      return {
        ...state,
        ui: { ...state.ui, columnFilters: newFilters },
      };
    }
    
    case ActionTypes.SET_ALL_COLUMN_FILTERS:
      return {
        ...state,
        ui: { ...state.ui, columnFilters: action.payload },
      };
    
    case ActionTypes.SET_TEXT_FILTER: {
      const { columnId, filter } = action.payload;
      return {
        ...state,
        ui: {
          ...state.ui,
          textFilters: {
            ...state.ui.textFilters,
            [columnId]: filter,
          },
        },
      };
    }
    
    case ActionTypes.CLEAR_TEXT_FILTER: {
      const newTextFilters = { ...state.ui.textFilters };
      delete newTextFilters[action.columnId];
      return {
        ...state,
        ui: { ...state.ui, textFilters: newTextFilters },
      };
    }
    
    case ActionTypes.CLEAR_ALL_FILTERS: {
      return {
        ...state,
        ui: { 
          ...state.ui, 
          columnFilters: {},
          textFilters: {},
          searchQuery: '',
        },
      };
    }
    
    case ActionTypes.TOGGLE_COLUMN_GROUP: {
      const groupName = action.payload;
      const isCollapsed = state.ui.collapsedGroups?.[groupName];
      return {
        ...state,
        ui: {
          ...state.ui,
          collapsedGroups: {
            ...state.ui.collapsedGroups,
            [groupName]: !isCollapsed,
          },
        },
      };
    }
    
    case ActionTypes.SET_COLLAPSED_GROUPS:
      return {
        ...state,
        ui: {
          ...state.ui,
          collapsedGroups: action.payload,
        },
      };
    
    case ActionTypes.SET_OPEN_FILTER_COLUMN:
      return {
        ...state,
        ui: { ...state.ui, openFilterColumn: action.columnId },
      };

    case ActionTypes.SET_SORT_CONFIG:
      return {
        ...state,
        ui: { ...state.ui, sortConfig: action.payload },
      };

    case ActionTypes.SET_SEARCH_QUERY:
      return {
        ...state,
        ui: { ...state.ui, searchQuery: action.payload },
      };
    
    case ActionTypes.SET_SELECTED_MATRIX:
      return {
        ...state,
        ui: {
          ...state.ui,
          selectedMatrixId: action.matrixId,
          selectedLineTag: action.lineTag,
        },
      };
    
    case ActionTypes.SET_UI:
      return {
        ...state,
        ui: { ...state.ui, ...action.payload },
      };
    
    // Job actions
    case ActionTypes.SET_JOB:
      return {
        ...state,
        jobs: { ...state.jobs, [action.jobType]: action.jobId },
      };
    
    case ActionTypes.CLEAR_JOB:
      return {
        ...state,
        jobs: { ...state.jobs, [action.jobType]: null },
      };
    
    // Toast actions
    case ActionTypes.SHOW_TOAST:
      return {
        ...state,
        toast: {
          show: true,
          message: action.message,
          type: action.toastType || 'success',
        },
      };
    
    case ActionTypes.HIDE_TOAST:
      return {
        ...state,
        toast: { ...state.toast, show: false },
      };
    
    // Change tracking
    case ActionTypes.ADD_CHANGED_ROW: {
      const newChangedRows = new Set(state.changedRows);
      newChangedRows.add(action.rowId);
      return {
        ...state,
        changedRows: newChangedRows,
        hasUnsavedChanges: true,
      };
    }
    
    case ActionTypes.REMOVE_CHANGED_ROW: {
      const newChangedRows = new Set(state.changedRows);
      newChangedRows.delete(action.rowId);
      return {
        ...state,
        changedRows: newChangedRows,
        hasUnsavedChanges: newChangedRows.size > 0,
      };
    }
    
    case ActionTypes.REMOVE_CHANGED_ROWS: {
      const newChangedRows = new Set(state.changedRows);
      action.rowIds.forEach(rowId => newChangedRows.delete(rowId));
      return {
        ...state,
        changedRows: newChangedRows,
        hasUnsavedChanges: newChangedRows.size > 0,
      };
    }
    
    case ActionTypes.CLEAR_CHANGED_ROWS:
      return {
        ...state,
        changedRows: new Set(),
        hasUnsavedChanges: false,
      };
    
    case ActionTypes.SET_CHANGED_ROWS:
      return {
        ...state,
        changedRows: action.payload,
        hasUnsavedChanges: action.payload.size > 0,
      };
    
    // Project actions
    case ActionTypes.SET_PROJECT_DETAILS:
      return {
        ...state,
        project: { ...state.project, details: action.payload },
      };
    
    case ActionTypes.SET_PROJECT_ID:
      return {
        ...state,
        project: { ...state.project, id: action.payload },
      };
    
    // Permission actions
    case ActionTypes.SET_PERMISSIONS:
      return {
        ...state,
        permissions: { ...state.permissions, ...action.payload },
      };
    
    case ActionTypes.SET_PERMISSION:
      return {
        ...state,
        permissions: { ...state.permissions, [action.key]: action.value },
      };
    
    // Batch update
    case ActionTypes.BATCH_UPDATE:
      return { ...state, ...action.payload };
    
    // Reset
    case ActionTypes.RESET:
      return { ...initialState, ...action.preserveKeys };
    
    // Row selection actions
    case ActionTypes.TOGGLE_ROW_SELECTION: {
      const newSelectedRows = new Set(state.ui.selectedRows);
      if (newSelectedRows.has(action.rowId)) {
        newSelectedRows.delete(action.rowId);
      } else {
        newSelectedRows.add(action.rowId);
      }
      return {
        ...state,
        ui: { ...state.ui, selectedRows: newSelectedRows },
      };
    }
    
    case ActionTypes.SELECT_ALL_ROWS: {
      const newSelectedRows = new Set(action.rowIds);
      return {
        ...state,
        ui: { ...state.ui, selectedRows: newSelectedRows },
      };
    }
    
    case ActionTypes.CLEAR_SELECTION:
      return {
        ...state,
        ui: { ...state.ui, selectedRows: new Set() },
      };
    
    case ActionTypes.SET_SELECTED_ROWS:
      return {
        ...state,
        ui: { ...state.ui, selectedRows: action.payload },
      };
    
    // Row removal (after delete)
    case ActionTypes.REMOVE_ROWS: {
      const idsToRemove = new Set(action.rowIds);
      const newRows = state.data.rows.filter(row => !idsToRemove.has(row.id));
      const newOriginal = state.data.original.filter(row => !idsToRemove.has(row.id));
      // Also clear selection for removed rows
      const newSelectedRows = new Set(state.ui.selectedRows);
      action.rowIds.forEach(id => newSelectedRows.delete(id));
      return {
        ...state,
        data: { ...state.data, rows: newRows, original: newOriginal },
        ui: { ...state.ui, selectedRows: newSelectedRows },
      };
    }
    
    default:
      console.warn(`Unknown action type: ${action.type}`);
      return state;
  }
}

/**
 * Action creators for common operations
 */
export const actions = {
  // Data
  setRows: (rows) => ({ type: ActionTypes.SET_ROWS, payload: rows }),
  setOriginalData: (data) => ({ type: ActionTypes.SET_ORIGINAL_DATA, payload: data }),
  setPriorities: (priorities) => ({ type: ActionTypes.SET_PRIORITIES, payload: priorities }),
  setFabricationLocations: (locations) => ({ type: ActionTypes.SET_FABRICATION_LOCATIONS, payload: locations }),
  setColumnMappings: (mappings) => ({ type: ActionTypes.SET_COLUMN_MAPPINGS, payload: mappings }),
  updateRow: (rowIndex, columnId, value) => ({
    type: ActionTypes.UPDATE_ROW,
    payload: { rowIndex, columnId, value },
  }),
  updateRowById: (rowId, columnId, value) => ({
    type: ActionTypes.UPDATE_ROW_BY_ID,
    payload: { rowId, columnId, value },
  }),
  
  // Loading
  setLoading: (key, value) => ({ type: ActionTypes.SET_LOADING, key, value }),
  
  // Modals
  openModal: (modal) => ({ type: ActionTypes.OPEN_MODAL, modal }),
  closeModal: (modal) => ({ type: ActionTypes.CLOSE_MODAL, modal }),
  toggleModal: (modal) => ({ type: ActionTypes.TOGGLE_MODAL, modal }),
  
  // UI
  setColumnFilter: (columnId, values) => ({
    type: ActionTypes.SET_COLUMN_FILTER,
    payload: { columnId, values },
  }),
  clearColumnFilter: (columnId) => ({ type: ActionTypes.CLEAR_COLUMN_FILTER, columnId }),
  setTextFilter: (columnId, filter) => ({
    type: ActionTypes.SET_TEXT_FILTER,
    payload: { columnId, filter },
  }),
  clearTextFilter: (columnId) => ({ type: ActionTypes.CLEAR_TEXT_FILTER, columnId }),
  clearAllFilters: () => ({ type: ActionTypes.CLEAR_ALL_FILTERS }),
  setCollapsedGroups: (groups) => ({ type: ActionTypes.SET_COLLAPSED_GROUPS, payload: groups }),
  setOpenFilterColumn: (columnId) => ({ type: ActionTypes.SET_OPEN_FILTER_COLUMN, columnId }),
  setSortConfig: (key, direction) => ({ type: ActionTypes.SET_SORT_CONFIG, payload: { key, direction } }),
  setSearchQuery: (query) => ({ type: ActionTypes.SET_SEARCH_QUERY, payload: query }),
  setSelectedMatrix: (matrixId, lineTag) => ({
    type: ActionTypes.SET_SELECTED_MATRIX,
    matrixId,
    lineTag,
  }),
  
  // Jobs
  setJob: (jobType, jobId) => ({ type: ActionTypes.SET_JOB, jobType, jobId }),
  clearJob: (jobType) => ({ type: ActionTypes.CLEAR_JOB, jobType }),
  
  // Toast
  showToast: (message, toastType = 'success') => ({
    type: ActionTypes.SHOW_TOAST,
    message,
    toastType,
  }),
  hideToast: () => ({ type: ActionTypes.HIDE_TOAST }),
  
  // Change tracking
  addChangedRow: (rowId) => ({ type: ActionTypes.ADD_CHANGED_ROW, rowId }),
  removeChangedRow: (rowId) => ({ type: ActionTypes.REMOVE_CHANGED_ROW, rowId }),
  removeChangedRows: (rowIds) => ({ type: ActionTypes.REMOVE_CHANGED_ROWS, rowIds }),
  clearChangedRows: () => ({ type: ActionTypes.CLEAR_CHANGED_ROWS }),
  
  // Project
  setProjectDetails: (details) => ({ type: ActionTypes.SET_PROJECT_DETAILS, payload: details }),
  setProjectId: (id) => ({ type: ActionTypes.SET_PROJECT_ID, payload: id }),
  
  // Permissions
  setPermissions: (permissions) => ({ type: ActionTypes.SET_PERMISSIONS, payload: permissions }),
  setPermission: (key, value) => ({ type: ActionTypes.SET_PERMISSION, key, value }),
  
  // Batch
  batchUpdate: (payload) => ({ type: ActionTypes.BATCH_UPDATE, payload }),
  
  // Reset
  reset: (preserveKeys = {}) => ({ type: ActionTypes.RESET, preserveKeys }),
  
  // Row selection
  toggleRowSelection: (rowId) => ({ type: ActionTypes.TOGGLE_ROW_SELECTION, rowId }),
  selectAllRows: (rowIds) => ({ type: ActionTypes.SELECT_ALL_ROWS, rowIds }),
  clearSelection: () => ({ type: ActionTypes.CLEAR_SELECTION }),
  setSelectedRows: (selectedRows) => ({ type: ActionTypes.SET_SELECTED_ROWS, payload: selectedRows }),
  
  // Row removal
  removeRows: (rowIds) => ({ type: ActionTypes.REMOVE_ROWS, rowIds }),
};

export default matrixReducer;

